import { useCallback, useEffect, useState } from 'react';
import { fetchActiveResult } from '../services/activeResult';
import type { ScopeFetch } from '../shell/cepiScope';

type Settled = Exclude<ScopeFetch, { status: 'none' | 'loading' }>;

/**
 * The thread's attachment as a three-way state. `fetchActiveResult` returning
 * null means the request FAILED, which is not "nothing attached" (that is a
 * `missing` result), so it maps to `error`, never to loading or corpus.
 *
 * The settled value is keyed by the inputs it answered, so on the render where
 * threadId or a token changes the hook reports `loading` immediately instead
 * of one frame of the previous answer.
 */
export function useActiveResult(
  threadId: string | null,
  refreshToken: number,
): { fetch: ScopeFetch; retry: () => void } {
  const [retryToken, setRetryToken] = useState(0);
  const [settled, setSettled] = useState<{ key: string; value: Settled } | null>(null);
  const key = `${threadId}|${refreshToken}|${retryToken}`;

  useEffect(() => {
    if (!threadId) return;
    let cancelled = false;
    void (async () => {
      const result = await fetchActiveResult(threadId);
      if (cancelled) return;
      setSettled({
        key,
        value: result ? { status: 'loaded', result } : { status: 'error' },
      });
    })();
    return () => { cancelled = true; };
  }, [threadId, key]);

  const retry = useCallback(() => setRetryToken((n) => n + 1), []);
  if (!threadId) return { fetch: { status: 'none' }, retry };
  return {
    fetch: settled && settled.key === key ? settled.value : { status: 'loading' },
    retry,
  };
}
